import { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useDidUpdateEffect from '../../../common/hooks/useDidUpdateEffect'
import { Element } from '../../../common/html/element'
import { InputDropdown } from '../../../common/html/input/InputDropdown'
import { debounce } from '../../../helpers/common'
import { ALERT_LIST_CONTAINER_SCROLL_ID, SEARCH_OPTIONS } from '../constant'
import {
  changeFilter,
  changeLoading,
  changeSearchTerm,
  selectFilter,
  selectSearchSubject,
  selectSearchTerm,
} from '../store/slice'
import { searchCompany, searchIndustry } from '../store/thunk'
import { SelectCustom } from './../../../common/selectCustom/'
import { CompanyList } from './CompanyList'
import { IndustryList } from './IndustryList'
import classes from './index.module.scss'

export const SearchBox = () => {
  const dispatch = useDispatch()

  const filter = useSelector(selectFilter())
  const term = useSelector(selectSearchTerm)
  const data = useSelector(selectSearchSubject)

  const [searchType, setSearchType] = useState(SEARCH_OPTIONS.COMPANY.value)
  const [isShowDropdown, setIsShowDropdown] = useState(false)

  const setTerm = (value) => {
    dispatch(changeSearchTerm(value))
  }

  const onChangeSearchType = ({ value }) => {
    setSearchType(value)
    setTerm('')
  }

  const onChangeTerm = (e) => {
    setTerm(e.target.value)
  }

  const dispatchSearch = useCallback(
    debounce((searchType, params) => {
      dispatch(
        searchType === SEARCH_OPTIONS.COMPANY.value
          ? searchCompany(params)
          : searchIndustry(params),
      )
    }, 500),
    [],
  )

  const handleSearch = () => {
    const { readStatus, subjectType, sentiment, startDate, endDate, criteria } =
      filter
    const params = {
      term,
      readStatus: readStatus.length === 1 ? readStatus[0] : undefined,
      subjectType: subjectType.length ? subjectType : undefined,
      sentimentId: sentiment.length ? sentiment : undefined,
      alertCriteriaId: criteria,
      fromDate: startDate,
      toDate: endDate,
      page: 1,
      pageSize: 10,
    }
    dispatchSearch(searchType, params)
  }

  const handleNoTerm = useCallback(
    debounce((payload) => {
      dispatch(changeFilter(payload))
    }, 500),
    [],
  )

  const onChooseSubject = (index) => {
    const item = data[index]
    if (!item) return

    const payload =
      searchType === SEARCH_OPTIONS.COMPANY.value
        ? { organizationId: item.organizationId }
        : { industryId: item.vsicId }

    dispatch(changeFilter({ ...payload, page: 1 }))
    setTerm(
      searchType === SEARCH_OPTIONS.COMPANY.value
        ? [item.taxCode, item.ticker, item.organizationName]
            .filter(Boolean)
            .join(' - ')
        : [item.vsicCode, item.vsicName].filter(Boolean).join(' - '),
    )
    setIsShowDropdown(false)
  }

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      onChooseSubject(0)
    }
  }

  const onClick = () => {
    handleSearch()
    setIsShowDropdown(true)
  }

  const onClickOutsideDropdown = () => {
    setIsShowDropdown(false)
    dispatch(changeLoading({ searchSubject: true }))
  }

  useDidUpdateEffect(() => {
    handleSearch()
    if (!term) {
      const payload = { organizationId: null, industryId: null }
      if (filter.organizationId || filter.industryId) {
        payload.page = 1
      }
      handleNoTerm(payload)
    }
  }, [searchType, term])

  return (
    <Element className={classes.searchBox}>
      <SelectCustom
        activeItem={{ value: searchType }}
        data={Object.values(SEARCH_OPTIONS)}
        chooseItem={onChangeSearchType}
        isI18n
        className="fw600"
        style={{ backgroundColor: '#E5E5E8', color: '#3A414B' }}
        scrollId={ALERT_LIST_CONTAINER_SCROLL_ID}
      />
      <InputDropdown
        placeHolder={
          searchType === SEARCH_OPTIONS.COMPANY.value
            ? 'alerts.SEARCH_COMPANY_PLACEHOLDER'
            : 'alerts.SEARCH_INDUSTRY_PLACEHOLDER'
        }
        type="text"
        containerStyle={{ width: '100%', minWidth: 180 }}
        isShowDropdown={isShowDropdown}
        onClickOutsideDropdown={onClickOutsideDropdown}
        onClick={onClick}
        onKeyDown={onKeyDown}
        value={term}
        onChange={onChangeTerm}
        inputProps={{ noControl: true }}
      >
        {searchType === SEARCH_OPTIONS.COMPANY.value ? (
          <CompanyList
            term={term}
            data={data}
            onChooseSubject={onChooseSubject}
          />
        ) : (
          <IndustryList
            term={term}
            data={data}
            onChooseSubject={onChooseSubject}
          />
        )}
      </InputDropdown>
    </Element>
  )
}
